import { atom } from 'jotai';
import { availableEventPeriodsAtom } from '@/atoms/queries/availableEventPeriods';
import { competitionsAtom } from '@/atoms/queries/competitions';
import { allSquadPlayerOptionsAtom } from '@/atoms/queries/players';
import { seasonsAtom } from '@/atoms/queries/seasons';

export type CompetitionInfo = { id: number; name: string; country: string };
export type SeasonInfo = { id: number; name: string };
export type SquadPlayerInfo = { id: string; name: string };

/**
 * Smart context injection for the AI filter prompt.
 *
 * All four sources are awaited directly so the atom suspends until every
 * piece of context is ready — competitions, seasons, and match periods are
 * already populated by the time the player vis page renders (they drive the
 * filter dropdowns), and the squad must be complete before the AI can
 * resolve recipient players by name.
 */
export const aiFilterContextInfosAtom = atom(async get => {
  const [competitions, seasons, availableMatchPeriods, squadPlayers] = await Promise.all([
    get(competitionsAtom),
    get(seasonsAtom),
    get(availableEventPeriodsAtom),
    get(allSquadPlayerOptionsAtom),
  ]);

  const competitionInfos: CompetitionInfo[] = competitions.map(competition => ({
    id: competition.competition_id,
    name: competition.name,
    // parachute-types doesn't carry a country name today — `area_code`
    // (e.g. "ENG") is the closest disambiguator.
    country: competition.area_code ?? '',
  }));
  const seasonInfos: SeasonInfo[] = seasons.map(season => ({
    id: season.season_id,
    name: season.name,
  }));
  const squadPlayerInfos: SquadPlayerInfo[] = squadPlayers.map(({ value: id, label: name }) => ({ id, name }));

  return { competitionInfos, seasonInfos, availableMatchPeriods, squadPlayerInfos };
});
